import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppLoadingState from './common/AppLoadingState';
import api from '../utils/api';
import { AppPageShell, PageHeader, Button, Panel } from './common/ui';
import { HiChatAlt2, HiClock, HiCollection, HiCreditCard, HiCubeTransparent, HiPhoneOutgoing, HiSparkles } from 'react-icons/hi';

const StatCard = ({ title, value, description, onClick, clickable = false, highlight = false, icon }) => (
  <div 
    className={`metric-card group ${
      clickable ? 'cursor-pointer' : ''
    } ${highlight ? 'metric-card-highlight' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between gap-4">
      <h3 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500">{title}</h3>
      {icon && <div className="metric-icon">{icon}</div>}
    </div>
    <div className="mt-5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <p className={`text-3xl font-black tracking-tight ${highlight ? 'text-slate-950' : 'text-slate-900'}`}>
        {value}
      </p>
      {description && (
        <p className="text-sm text-slate-500">{description}</p>
      )}
    </div>
  </div>
);

const formatMinutes = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.floor(totalMinutes % 60);
  const seconds = Math.round((totalMinutes % 1) * 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m${seconds > 0 ? ` ${seconds}s` : ''}`;
  }
  return `${minutes}m${seconds > 0 ? ` ${seconds}s` : ''}`;
};

const formatCallTime = (totalCallTime) => {
  const totalSeconds = Math.round((totalCallTime || 0) * 60);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m${seconds > 0 ? ` ${seconds}s` : ''}`;
  }
  if (minutes > 0) {
    return `${minutes}m${seconds > 0 ? ` ${seconds}s` : ''}`;
  }
  return seconds > 0 ? `${seconds}s` : '0m';
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    activeCalls: 0,
    completedCalls: 0,
    dialogFlows: [],
    totalCallTime: 0,
    credits: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const [activeCalls, completedCalls, dialogFlows, totalCallTime, creditData] = await Promise.allSettled([
          api.getCallsWithFilters({ completed: false }),
          api.getCallsWithFilters({ completed: true }),
          api.getdialogFlows(),
          api.getTotalCallTime(),
          api.getCreditBalance()
        ]);

        // Handle each promise result individually
        setStats({
          activeCalls: activeCalls.status === 'fulfilled' ? activeCalls.value.length : 0,
          completedCalls: completedCalls.status === 'fulfilled' ? completedCalls.value.length : 0,
          dialogFlows: dialogFlows.status === 'fulfilled' ? dialogFlows.value : [],
          totalCallTime: totalCallTime.status === 'fulfilled' ? totalCallTime.value.total_call_time : 0,
          credits: creditData.status === 'fulfilled' ? creditData.value.credits : 0
        });

        // Log any errors that occurred
        [activeCalls, completedCalls, dialogFlows, totalCallTime, creditData].forEach((result, index) => {
          if (result.status === 'rejected') {
            console.error(`Error fetching data for index ${index}:`, result.reason);
          }
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return <AppLoadingState layout="section" showLabel={false} />;
  }

  return (
    <AppPageShell>
      <div className="space-y-8">
      <PageHeader
        lead={
          <div className="inline-flex items-center gap-2 rounded-full border border-[var(--app-border)] bg-[var(--app-surface-veil)] px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-[var(--app-muted)]">
            <HiSparkles className="h-4 w-4 text-[var(--app-warm)]" />
            Live workspace
          </div>
        }
        title={
          <>
            Welcome back<span className="text-[var(--app-muted)]">,</span>
          </>
        }
        subtitle={<p className="dashboard-hero-user">{user?.email}</p>}
        description="Monitor calls, launch conversations, and manage agent resources from one operational dashboard."
        aside={
          <div className="dashboard-hero-panel">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
              Credits available
            </span>
            <strong>{formatMinutes(stats.credits)}</strong>
            <Button onClick={() => navigate('/app/credits')} className="mt-5 w-full">
              Manage Credits
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <StatCard
          title="Credits"
          value={formatMinutes(stats.credits)}
          clickable
          highlight
          icon={<HiCreditCard className="w-5 h-5" />}
          onClick={() => navigate('/app/credits')}
        />
        <StatCard
          title="Active Calls"
          value={stats.activeCalls}
          description="in progress"
          clickable
          icon={<HiPhoneOutgoing className="w-5 h-5" />}
          onClick={() => navigate('/app/calls')}
        />
        <StatCard
          title="Completed"
          value={stats.completedCalls}
          clickable
          icon={<HiCollection className="w-5 h-5" />}
          onClick={() => navigate('/app/calls')}
        />
        <StatCard
          title="Call Time"
          value={formatCallTime(stats.totalCallTime)}
          clickable
          icon={<HiClock className="w-5 h-5" />}
          onClick={() => navigate('/app/calls')}
        />
        <StatCard
          title="Dialog Flows"
          value={stats.dialogFlows.length}
          clickable
          icon={<HiCubeTransparent className="w-5 h-5" />}
          onClick={() => navigate('/app/dialog-flows')}
        />
      </div>

      <Panel>
        <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-lg font-black tracking-tight text-slate-950">Quick Actions</h2>
            <p className="text-sm text-slate-500">
              High-frequency workflows, ready from the first screen.
            </p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <Button onClick={() => navigate('/app/send-call')} variant="tile">
            <HiPhoneOutgoing className="w-5 h-5" />
            <span>New Call</span>
          </Button>
          <Button onClick={() => navigate('/app/dialog-flows')} variant="tile">
            <HiCubeTransparent className="w-5 h-5" />
            <span>Create Dialog Flow</span>
          </Button>
          <Button onClick={() => navigate('/app/dialog-flows/chats')} variant="tile">
            <HiChatAlt2 className="w-5 h-5" />
            <span>Start Chat</span>
          </Button>
        </div>
      </Panel>

      {stats.dialogFlows.length > 0 && (
        <Panel>
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="text-lg font-black tracking-tight text-slate-950">Recent Dialog Flows</h2>
              <p className="text-sm text-slate-500">Continue work on your most recent flows.</p>
            </div>
            <Button onClick={() => navigate('/app/dialog-flows')} variant="secondary">
              View All
            </Button>
          </div>
          <div className="mt-5 divide-y divide-slate-200/80 overflow-hidden rounded-lg border border-slate-200/80 bg-white/70">
            {stats.dialogFlows.slice(0, 5).map((dialogFlow) => (
              <button
                key={dialogFlow.id}
                className="w-full p-4 text-left hover:bg-white transition-colors"
                onClick={() => navigate(`/app/dialog-flows/${dialogFlow.id}`)}
              >
                <h3 className="font-bold text-slate-900">
                  {dialogFlow.name || 'Unnamed Dialog Flow'}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  {dialogFlow.description || 'No description available'}
                </p>
              </button>
            ))}
          </div>
        </Panel>
      )}
      </div>
    </AppPageShell>
  );
};

export default Dashboard; 
